import { withConsoleBase } from '../config';
import { assertCurrentSessionGeneration, notifySessionExpired, sessionGeneration, StaleSessionError } from '../lib/session-events';
import { apolloReasons } from '../lib/apollo';
import type {
  AgentRun, AuditEvent, BacklogItem, Capabilities, CommercialSnapshot, DecisionItem, PrivatePreview, PrivatePreviewSummary,
  ContentPublishResponse, Experiment, ListResponse, ManagementJob, MetricSnapshot, OutboxItem, PublicationDraft,
  Report, ReportSummary, SessionResponse, StatusResponse, WorkOrder, WorkPageResponse, AgentGuidance, MailStatus, MailThread, MailThreadSummary, Prospect, ProspectPage, ProspectConcept, ApolloBudget, ApolloAutomation, ApolloProspectResult, ApolloCredentialMetadata, ApolloFoundContact, ApolloContactReview
} from '../types';

const API_ROOT = '/console-api/v1';
type JsonRecord = Record<string, unknown>;
interface RawPage { items?: JsonRecord[] }
export type ListOptions = { cursor?: string | null; [key: string]: string | null | undefined };
function listUrl(path: string, options: ListOptions = {}) {
  const params = new URLSearchParams({ limit: '25' });
  Object.entries(options).forEach(([key, value]) => { if (value) params.set(key, value); });
  return API_ROOT + '/' + path + '?' + params.toString();
}
async function listPage<T>(path: string, options: ListOptions, normalize: (item: JsonRecord) => T): Promise<ListResponse<T>> {
  const result = await rawPage(listUrl(path, options));
  return { items: pageItems(result).map(normalize), nextCursor: nullableText(record(record(result).page).next_cursor) };
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}
function nullableText(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}
function number(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function record(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {};
}
function pageItems(value: unknown): JsonRecord[] {
  const items = record(value).items;
  return Array.isArray(items) ? items.filter((item): item is JsonRecord => !!item && typeof item === 'object' && !Array.isArray(item)) : [];
}
function numericMetrics(value: unknown): Record<string, number | null> {
  return Object.fromEntries(Object.entries(record(value)).filter((entry): entry is [string, number | null] => entry[1] === null || (typeof entry[1] === 'number' && Number.isFinite(entry[1]))));
}
function metricDaily(value: unknown): MetricSnapshot['daily'] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 366).map(record).flatMap((item) => {
    const date = text(item.date);
    return /^\d{4}-\d{2}-\d{2}$/.test(date) ? [{ date, metrics: numericMetrics(item.metrics) }] : [];
  });
}
function metricBreakdown(value: unknown, key: string, maximum: number): MetricSnapshot['breakdowns']['pages'] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, maximum).map(record).flatMap((item) => {
    const label = text(item[key]);
    if (!label) return [];
    const metrics = numericMetrics(item);
    delete metrics.combined_groups;
    return [{
      label,
      metrics,
      ...(typeof item.combined_groups === 'number' ? { combinedGroups: item.combined_groups } : {})
    }];
  });
}
function publication(value: unknown): PublicationDraft | undefined {
  const outer = record(value);
  const nested = record(outer.publication);
  const candidate = Object.keys(nested).length ? nested : outer;
  if (!candidate.slug && !candidate.title) return undefined;
  return {
    slug: text(candidate.slug), title: text(candidate.title), summary: text(candidate.excerpt, text(candidate.summary)),
    category: (['guides', 'product', 'partners', 'company-news'].includes(text(candidate.category)) ? candidate.category : 'company-news') as PublicationDraft['category'],
    authorName: text(candidate.author_name), markdownBody: text(candidate.markdown_body),
    seoTitle: text(candidate.seo_title), seoDescription: text(candidate.seo_description),
    heroImageUrl: text(candidate.hero_image_url),
    heroImageAlt: text(candidate.hero_image_alt),
    sourceUrls: Array.isArray(candidate.source_urls) ? candidate.source_urls.filter((value): value is string => typeof value === 'string') : [],
    state: candidate.state === 'published' ? 'published' : candidate.state === 'draft' ? 'draft' : undefined,
    expectedRevision: typeof candidate.expected_revision === 'number' ? candidate.expected_revision : typeof candidate.expectedRevision === 'number' ? candidate.expectedRevision : undefined
  };
}
function normalizeRun(item: JsonRecord) {
  const rawStatus = text(item.status, 'QUEUED').toUpperCase();
  const status = ['SUCCESS', 'SUCCEEDED'].includes(rawStatus) ? 'COMPLETE' : rawStatus === 'PENDING' ? 'QUEUED' : rawStatus;
  return {
    id: number(item.id), agent: text(item.agent, 'unknown'),
    status: (['QUEUED', 'RUNNING', 'COMPLETE', 'FAILED'].includes(status) ? status : 'FAILED') as 'QUEUED' | 'RUNNING' | 'COMPLETE' | 'FAILED',
    startedAt: text(item.started_at), completedAt: nullableText(item.completed_at),
    reportId: item.has_report ? number(item.id) : undefined,
    error: item.failed ? 'Run failed; inspect bounded service diagnostics.' : undefined
  };
}

export function normalizeMetricEnvelope(item: JsonRecord): MetricSnapshot {
  const provenance = record(item.provenance);
  const normalized = record(item.normalized);
  const freshThrough = nullableText(item.fresh_through) || nullableText(record(normalized.observation).observed_through);
  const weekly = record(normalized.series).weekly_active_venues;
  const completeWeeks = (Array.isArray(weekly) ? weekly.map(record) : []).filter(bucket =>
    bucket.complete_seven_day_window === true && typeof bucket.value === 'number' && Number.isFinite(bucket.value) &&
    freshThrough && Date.parse(text(bucket.end)) <= Date.parse(freshThrough));
  completeWeeks.sort((a, b) => text(b.end).localeCompare(text(a.end)));
  const week = completeWeeks[0];
  const population = text(provenance.population_description) || text(provenance.population) || 'Aggregate source population (see source provenance)';
  return {
    id: number(item.id), source: text(item.source, 'unknown'), recordedAt: text(item.collected_at),
    freshThrough, completeWeek: week ? { start: text(week.start), end: text(week.end), value: number(week.value) } : null,
    windowStart: nullableText(item.window_start), windowEnd: nullableText(item.window_end),
    population,
    metrics: { ...numericMetrics(normalized.totals), ...numericMetrics(normalized.metrics) },
    daily: metricDaily(normalized.daily),
    breakdowns: {
      pages: metricBreakdown(normalized.pages, 'path', 100),
      content: metricBreakdown(normalized.content, 'content_slug', 100),
      origins: metricBreakdown(normalized.origins, 'origin_category', 16),
      countries: metricBreakdown(normalized.countries, 'country_code', 260)
    }
  };
}
function normalizeExperiment(item: JsonRecord): Experiment {
  return {
    id: text(item.id), title: text(item.title, 'Untitled experiment'), hypothesis: text(item.hypothesis),
    ownerAgent: text(item.owner_agent, 'unassigned'), primaryMetric: text(item.primary_metric, 'unspecified'),
    status: text(item.status, 'UNKNOWN'), targetValue: number(item.target_value), latestValue: null,
    decisionDueAt: text(item.decision_due_at)
  };
}
function normalizeOutbox(item: JsonRecord): OutboxItem {
  const approvedPayload = record(item.approved_payload);
  const reviewHistory = Array.isArray(item.review_history) ? item.review_history.map(record) : [];
  return {
    id: number(item.id), kind: text(item.kind, 'UNKNOWN'), status: text(item.status, 'UNKNOWN'),
    target: text(item.target), subject: text(item.subject), body: text(item.body),
    payloadSha256: text(item.payload_sha256),
    approvedPayloadSha256: nullableText(item.approved_payload_sha256) || undefined,
    approvedBy: nullableText(item.approved_by) || undefined,
    approvedAt: nullableText(item.approved_at) || undefined,
    approvedPublicationState: approvedPayload.state === 'published' ? 'published' : approvedPayload.state === 'draft' ? 'draft' : undefined,
    publication: publication(record(item.payload)),
    reviewHistory: reviewHistory.map((review) => ({
      id: number(review.id), reviewedAt: text(review.reviewed_at), reviewer: text(review.reviewer),
      reviewerModel: text(review.reviewer_model),
      verdict: (review.verdict === 'APPROVE' ? 'APPROVE' : 'REJECT') as 'APPROVE' | 'REJECT',
      reasonCode: text(review.reason_code), rationale: text(review.rationale),
      sourcePayloadSha256: text(review.source_payload_sha256)
    })),
    updatedAt: text(item.updated_at, text(item.created_at))
  };
}

export function normalizeDashboard(dashboardValue: unknown, capabilitiesValue: unknown): StatusResponse {
  const dashboard = record(dashboardValue);
  const counts = record(dashboard.counts);
  const flags = record(record(capabilitiesValue).flags);
  const agents = record(capabilitiesValue).agents;
  const metricSources = Array.isArray(dashboard.metric_sources) ? dashboard.metric_sources.map(record) : [];
  const schedules = Array.isArray(dashboard.schedules) ? dashboard.schedules.map(record) : [];
  const recentRuns = Array.isArray(dashboard.recent_runs) ? dashboard.recent_runs.map(record) : [];
  return {
    northStar: text(dashboard.north_star, 'North-star metric unavailable'), latestMetrics: {},
    metricSources: metricSources.map((item) => ({
      source: text(item.source, 'unknown'),
      status: (text(item.status, item.envelope_id ? 'STALE' : 'NOT_COLLECTED').toUpperCase() || 'NOT_COLLECTED') as 'COMPLETE' | 'STALE' | 'FAILED' | 'NOT_COLLECTED',
      collectedAt: nullableText(item.collected_at), windowStart: null, windowEnd: nullableText(item.window_end),
      population: 'Aggregate source population (see metric history)',
      detail: nullableText(item.last_failure_category) || undefined
    })),
    recentRuns: recentRuns.map(normalizeRun),
    scheduledJobs: schedules.map((item) => ({
      id: text(item.id), nextRun: nullableText(item.next_run_at),
      enabled: item.enabled === true, running: item.scheduler_running === true
    })).sort((a, b) => (Date.parse(a.nextRun || '') || Infinity) - (Date.parse(b.nextRun || '') || Infinity)),
    schedulerEnabled: typeof flags.scheduler_enabled === 'boolean'
      ? flags.scheduler_enabled : schedules.some((item) => item.enabled === true),
    schedulerRunning: typeof flags.scheduler_running === 'boolean'
      ? flags.scheduler_running : schedules.length ? schedules.some((item) => item.scheduler_running === true) : null,
    contentPublishingEnabled: flags.content_publishing_enabled === true,
    agentContentAutoPublishEnabled: flags.agent_content_auto_publish_enabled === true,
    activeExperimentCount: number(counts.active_experiments),
    queuedJobCount: number(counts.queued_jobs),
    runningJobCount: number(counts.running_jobs),
    outboxCounts: { PENDING: number(counts.pending_outbox), FAILED_REVIEW: number(counts.failed_review_outbox) },
    workCounts: Object.fromEntries([
      ['running', 'work_running'], ['awaitingDirector', 'work_awaiting_director'],
      ['needsAttention', 'work_needs_attention'], ['completedToday', 'work_completed_today']
    ].map(([key, raw]) => [key, typeof counts[raw] === 'number' && Number.isSafeInteger(counts[raw]) && Number(counts[raw]) >= 0 ? counts[raw] : null])) as StatusResponse['workCounts'],
    agents: Array.isArray(agents) ? agents.filter((value): value is string => typeof value === 'string') : []
  };
}

function normalizeJob(value: unknown): ManagementJob {
  const item = record(value);
  const rawStatus = text(item.status, 'PENDING').toUpperCase();
  return {
    id: number(item.id),
    kind: (item.kind === 'METRIC_COLLECTION' ? 'METRIC_COLLECTION' : item.kind === 'PROSPECT_RESEARCH' ? 'PROSPECT_RESEARCH' : item.kind === 'PROSPECT_PREVIEW' ? 'PROSPECT_PREVIEW' : item.kind === 'PROSPECT_OUTREACH_REVIEW' ? 'PROSPECT_OUTREACH_REVIEW' : item.kind === 'PROSPECT_ENRICHMENT' ? 'PROSPECT_ENRICHMENT' : item.kind === 'APOLLO_CONTACT_REVIEW' ? 'APOLLO_CONTACT_REVIEW' : 'AGENT_RUN'),
    target: text(item.target),
    status: (['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED'].includes(rawStatus) ? rawStatus : 'FAILED') as ManagementJob['status'],
    createdAt: text(item.created_at), startedAt: nullableText(item.started_at),
    completedAt: nullableText(item.completed_at), errorCode: nullableText(item.error_code) || undefined
  };
}

function creditNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

export function normalizeApolloBudget(value: unknown): ApolloBudget {
  const item = record(value);
  return {
    mode: item.mode === 'NO_SEND_PILOT' ? 'NO_SEND_PILOT' : null,
    discovery_policy: item.discovery_policy === undefined ? 'pilot-v1' : ['pilot-v1', 'expanded-v2'].includes(text(item.discovery_policy)) ? item.discovery_policy as 'pilot-v1' | 'expanded-v2' : null,
    monthly_attempt_limit: creditNumber(item.monthly_attempt_limit),
    enabled: item.enabled === true, pilot_enabled: item.pilot_enabled === true,
    contact_review_enabled: item.contact_review_enabled === true,
    pricing_confirmed: item.pricing_confirmed === true, configured: item.configured === true, ready: item.ready === true,
    reason_code: nullableText(item.reason_code), month: /^\d{4}-(0[1-9]|1[0-2])$/.test(text(item.month)) ? text(item.month) : null,
    deferred_until: nullableText(item.deferred_until),
    monthly_limit: creditNumber(item.monthly_limit), enrichment_limit: creditNumber(item.enrichment_limit),
    charged_credits: creditNumber(item.charged_credits), reserved_credits: creditNumber(item.reserved_credits), uncertain_credits: creditNumber(item.uncertain_credits),
    used_credits: creditNumber(item.used_credits), remaining_credits: creditNumber(item.remaining_credits), enrichment_remaining_credits: creditNumber(item.enrichment_remaining_credits),
    results_count: creditNumber(item.results_count), matched_count: creditNumber(item.matched_count), per_person_credit_ceiling: creditNumber(item.per_person_credit_ceiling),
    automation: normalizeApolloAutomation(item.automation)
  };
}

export function normalizeApolloAutomation(value: unknown): ApolloAutomation | null {
  const item = record(value);
  const policy = item.discovery_policy === undefined ? 'pilot-v1' : item.discovery_policy;
  const expanded = policy === 'expanded-v2';
  if (typeof item.enabled !== 'boolean' || typeof item.ready !== 'boolean'
    || !['pilot-v1', 'expanded-v2'].includes(String(policy))
    || !(item.paused === null || typeof item.paused === 'boolean')
    || !Number.isSafeInteger(item.daily_limit) || Number(item.daily_limit) < 1 || Number(item.daily_limit) > (expanded ? 20 : 2)
    || (expanded && item.cadence_minutes !== 15) || (!expanded && item.cadence_minutes !== undefined && item.cadence_minutes !== 60)
    || item.per_cycle_limit !== 1 || item.schedule_id !== 'apollo-auto-lookup') return null;
  const count = (value: unknown) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
  if (item.ready && (!item.enabled || item.paused !== false || item.reason_code !== null
    || (count(item.eligible_count) ?? 0) < 1 || (count(item.remaining_today) ?? 0) < 1 || item.active_count !== 0)) return null;
  const code = (value: unknown) => typeof value === 'string' && Object.prototype.hasOwnProperty.call(apolloReasons, value) ? value : null;
  return {
    discovery_policy: expanded ? 'expanded-v2' : 'pilot-v1', cadence_minutes: expanded ? 15 : 60,
    enabled: item.enabled, ready: item.ready, paused: item.paused, daily_limit: Number(item.daily_limit), per_cycle_limit: 1, schedule_id: 'apollo-auto-lookup',
    reason_code: code(item.reason_code), eligible_count: count(item.eligible_count), used_today: count(item.used_today), remaining_today: count(item.remaining_today), active_count: count(item.active_count),
    last_cycle_at: safeApolloDate(item.last_cycle_at), last_cycle_reason: code(item.last_cycle_reason), last_lookup_at: safeApolloDate(item.last_lookup_at),
    last_job_status: ['PENDING', 'RUNNING', 'SUCCEEDED', 'FAILED'].includes(text(item.last_job_status)) ? item.last_job_status as ApolloAutomation['last_job_status'] : null,
    last_outcome: ['PENDING', 'STAGED', 'NO_MATCH', 'NO_EMAIL', 'REJECTED', 'FAILED', 'UNCERTAIN'].includes(text(item.last_outcome)) ? item.last_outcome as ApolloAutomation['last_outcome'] : null,
    last_error_code: code(item.last_error_code)
  };
}

export function normalizeApolloProspect(value: unknown): ApolloProspectResult {
  const response = record(value), item = record(response.item), job = record(response.job), reviewJob = record(response.review_job), review = record(item.review);
  const reviewStatus = ['PENDING', 'RUNNING', 'APPROVED', 'REJECTED', 'BLOCKED'].includes(text(review.status)) ? review.status as ApolloContactReview['status'] : null;
  return {
    item: item.mode === 'NO_SEND_PILOT' && Number.isSafeInteger(item.id) && number(item.id) > 0 && Number.isSafeInteger(item.prospect_id) && number(item.prospect_id) > 0 ? {
      id: number(item.id), prospect_id: number(item.prospect_id), mode: 'NO_SEND_PILOT', outcome: text(item.outcome),
      role: nullableText(item.role), email_status: nullableText(item.email_status), checked_at: nullableText(item.checked_at), expires_at: nullableText(item.expires_at),
      error_code: nullableText(item.error_code), credit_state: nullableText(item.credit_state), credits_reserved: creditNumber(item.credits_reserved),
      review: reviewStatus ? { status: reviewStatus, reason_code: safeApolloCode(review.reason_code), reviewed_at: safeApolloDate(review.reviewed_at) } : null,
      discovery: normalizeApolloDiscovery(item.discovery),
    } : null,
    job: Number.isSafeInteger(job.id) && number(job.id) > 0 && job.kind === 'PROSPECT_ENRICHMENT' ? normalizeJob(job) : null,
    review_job: Number.isSafeInteger(reviewJob.id) && number(reviewJob.id) > 0 && reviewJob.kind === 'APOLLO_CONTACT_REVIEW' ? normalizeJob(reviewJob) : null
  };
}

function safeApolloCode(value: unknown): string | null {
  return typeof value === 'string' && ['apollo_contact_approved', 'apollo_contact_weak_fit', 'apollo_contact_role_unsuitable', 'apollo_contact_identity_unverified', 'apollo_contact_privacy_risk', 'apollo_contact_expired', 'apollo_contact_prospect_changed', 'apollo_contact_result_changed', 'apollo_contact_review_failed', 'apollo_contact_review_interrupted', 'apollo_contact_unavailable', 'apollo_contact_suppressed'].includes(value) ? value : null;
}

export function normalizeApolloDiscovery(value: unknown) {
  const item = record(value), counts = record(item.counts);
  const reasons = ['apollo_search_ready', 'apollo_search_empty', 'apollo_search_wrong_employer', 'apollo_search_unsuitable_role',
    'apollo_search_email_unavailable', 'apollo_search_invalid_people', 'apollo_candidates_exhausted',
    'apollo_search_no_eligible_people', 'apollo_shortlist_reused'];
  if (!reasons.includes(text(item.reason_code))) return null;
  return { reason_code: text(item.reason_code), counts: Object.fromEntries(
    ['requests', 'passes', 'rows', 'unique_people', 'duplicates', 'wrong_employer', 'unsuitable_role', 'email_unavailable',
      'invalid_people', 'previously_attempted', 'eligible_people', 'shortlisted'].map(key => [key,
        Number.isSafeInteger(counts[key]) && number(counts[key]) >= 0 && number(counts[key]) <= 1000 ? number(counts[key]) : null])) };
}
function safeApolloDate(value: unknown): string | null {
  return typeof value === 'string' && value.length <= 40 && /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value)) ? value : null;
}

export function normalizeApolloContact(value: unknown, prospectId: number, resultId: number): ApolloFoundContact {
  const item = record(record(value).item);
  const checked = safeApolloDate(item.checked_at), expires = safeApolloDate(item.expires_at);
  const firstName = item.first_name === undefined ? null : item.first_name;
  const nameSource = item.name_source === undefined ? 'NONE' : item.name_source;
  const validName = firstName === null ? nameSource === 'NONE' :
    typeof firstName === 'string' && Array.from(firstName).length <= 80 && /\p{L}/u.test(firstName) &&
    /^[\p{L}\p{M} '\-’]+$/u.test(firstName) && firstName === firstName.normalize('NFC').trim() &&
    !firstName.includes('  ') && nameSource === 'APOLLO';
  if (item.prospect_id !== prospectId || item.result_id !== resultId || !Number.isSafeInteger(resultId) || resultId <= 0 ||
    typeof item.email !== 'string' || item.email.length > 254 || !/^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/.test(item.email) ||
    typeof item.company_domain !== 'string' || item.company_domain.length > 253 || !/^[a-z0-9]+(?:[.-][a-z0-9-]+)*\.[a-z0-9-]+$/i.test(item.company_domain) || !checked || !expires || !validName) {
    throw new Error('The saved contact could not be confirmed. Refresh Apollo status before viewing it again.');
  }
  return { result_id: resultId, prospect_id: prospectId, email: item.email, company_domain: item.company_domain,
    first_name: firstName as string | null, name_source: nameSource as 'APOLLO' | 'NONE',
    role: ['PARTNERSHIPS', 'BUSINESS_DEVELOPMENT', 'SALES', 'OWNER_MANAGEMENT', 'VENUE_MANAGEMENT', 'OPERATIONS', 'MARKETING', 'TECHNOLOGY'].includes(text(item.role)) ? text(item.role) : null,
    email_status: ['VERIFIED', 'VALID', 'UNVERIFIED', 'UNAVAILABLE', 'UNKNOWN'].includes(text(item.email_status).toUpperCase()) ? text(item.email_status).toUpperCase() : null,
    checked_at: checked, expires_at: expires };
}

/** Private contact/error bodies must not enter React Query state or its retry cache. */
async function apolloPrivateContact(id: number, resultId: number, signal?: AbortSignal): Promise<ApolloFoundContact> {
  const generation = sessionGeneration();
  try {
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(resultId) || resultId <= 0) throw new Error('Invalid identity');
    const response = await requestJson<unknown>(API_ROOT + '/prospects/' + id + '/enrichment/apollo/contact', { cache: 'no-store', signal });
    assertCurrentSessionGeneration(generation);
    return normalizeApolloContact(response, id, resultId);
  } catch (error) {
    assertCurrentSessionGeneration(generation);
    if (error instanceof StaleSessionError) throw error;
    throw new ConsoleApiError(error instanceof ConsoleApiError ? error.status : 0, 'The saved contact could not be confirmed. Refresh Apollo status before viewing it again.');
  }
}

export function normalizeApolloCredentials(value: unknown): ApolloCredentialMetadata {
  const item = record(value);
  const source = item.source === 'NONE' || item.source === 'CONSOLE' || item.source === 'FILE' ? item.source : null;
  const revision = Number.isSafeInteger(item.revision) && number(item.revision, -1) >= 0 ? number(item.revision) : null;
  return { source, revision, stored: item.stored === true, usable: item.usable === true,
    encryption_ready: item.encryption_ready === true, editable: source !== null && revision !== null && item.editable === true,
    updated_at: typeof item.updated_at === 'string' && /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})$/.test(item.updated_at) && Number.isFinite(Date.parse(item.updated_at)) ? item.updated_at : null,
    reason_code: ['apollo_credential_file_managed', 'apollo_credential_disabled_required', 'apollo_credential_busy', 'apollo_credential_conflict', 'apollo_credential_unreadable', 'apollo_credential_audit_invalid', 'apollo_credential_unavailable', 'apollo_key_missing', 'apollo_key_invalid', 'apollo_encryption_unavailable'].includes(text(item.reason_code)) ? text(item.reason_code) : null };
}

/** Credentials never enter a mutation cache; discard any raw error body here. */
async function apolloCredentialRequest(init: RequestInit = {}, csrfToken?: string): Promise<ApolloCredentialMetadata> {
  const generation = sessionGeneration();
  try {
    const result = await requestJson<unknown>(API_ROOT + '/prospects/enrichment/apollo/credentials', init, csrfToken);
    assertCurrentSessionGeneration(generation);
    return normalizeApolloCredentials(result);
  } catch (error) {
    assertCurrentSessionGeneration(generation);
    if (error instanceof StaleSessionError) throw error;
    throw new ConsoleApiError(error instanceof ConsoleApiError ? error.status : 0, 'Apollo credential status could not be confirmed. Refresh its status before trying again.');
  }
}

function validCredentialRevision(value: number) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error('Current credential revision is required.');
}

export class ConsoleApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message); this.name = 'ConsoleApiError'; this.status = status; this.code = code;
  }
}

async function requestJson<T>(path: string, init: RequestInit = {}, csrfToken?: string): Promise<T> {
  if (!path.startsWith('/auth/') && !path.startsWith(API_ROOT + '/')) {
    throw new Error('Refusing request outside the same-origin console API');
  }
  const requestGeneration = sessionGeneration();
  const method = (init.method || 'GET').toUpperCase();
  const headers: Record<string, string> = { ...((init.headers || {}) as Record<string, string>), Accept: 'application/json' };
  if (init.body) headers['Content-Type'] = 'application/json';
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    if (!csrfToken) throw new Error('A CSRF token is required for console mutations');
    headers['X-CSRF-Token'] = csrfToken;
  }
  const response = await fetch(withConsoleBase(path), { ...init, headers, credentials: 'same-origin' });
  assertCurrentSessionGeneration(requestGeneration);
  if (!response.ok) {
    if (response.status === 401 && path.startsWith(API_ROOT + '/')) notifySessionExpired(requestGeneration);
    let message = 'The console request failed.';
    let code: string | undefined;
    try {
      const payload = record(await response.json());
      const upstreamError = record(payload.error);
      // Only these fixed field/action codes are used by the recovery UI. Never
      // retain arbitrary upstream diagnostics alongside private form values.
      const candidate = upstreamError.code ?? payload.code;
      if (typeof candidate === 'string' && ['contact_invalid', 'contact_domain_mismatch', 'contact_source_invalid', 'prospect_state_conflict', 'outreach_review_unavailable'].includes(candidate)) code = candidate;
      const detail = typeof payload.message === 'string' ? payload.message : typeof payload.detail === 'string' ? payload.detail : upstreamError.message;
      if (typeof detail === 'string' && detail.length <= 240) message = detail;
    } catch (_) { /* Deliberately return the bounded generic error. */ }
    throw new ConsoleApiError(response.status, message, code);
  }
  if (response.status === 204) return undefined as T;
  const result = await response.json() as T;
  assertCurrentSessionGeneration(requestGeneration);
  return result;
}
const rawPage = (path: string) => requestJson<RawPage>(path);

async function metricHistory(source?: string): Promise<ListResponse<MetricSnapshot>> {
  // The full 200-envelope response can exceed the BFF's unchanged 2 MiB guard.
  // Traverse the existing API in bounded pages; never show a partial traversal
  // as complete history, or mix responses from different authenticated sessions.
  const generation = sessionGeneration();
  const items: MetricSnapshot[] = [], seen = new Set<number>();
  const invalid = () => new ConsoleApiError(502, 'Metric history could not be verified. Refresh to try again.');
  let cursor: string | null = null;
  let previous: { windowEnd: string; id: number } | null = null;
  for (let pageNumber = 0; pageNumber < 4; pageNumber++) {
    assertCurrentSessionGeneration(generation);
    const params = new URLSearchParams({ limit: '50' });
    if (source) params.set('source', source);
    if (cursor !== null) params.set('cursor', cursor);
    const value = record(await requestJson<unknown>(API_ROOT + '/metrics/history?' + params.toString(), { cache: 'no-store' }));
    assertCurrentSessionGeneration(generation);
    const rows = value.items, page = record(value.page);
    if (!Array.isArray(rows) || rows.length > 50 || page.limit !== 50 || !Object.prototype.hasOwnProperty.call(page, 'next_cursor')) throw invalid();
    const next = page.next_cursor;
    if (next !== null && (typeof next !== 'string' || next !== String((pageNumber + 1) * 50) || rows.length !== 50)) throw invalid();
    for (const value of rows) {
      const row = record(value);
      if (!Number.isSafeInteger(row.id) || (row.id as number) <= 0 || seen.has(row.id as number) ||
          typeof row.window_end !== 'string' || !Number.isFinite(Date.parse(row.window_end)) ||
          typeof row.source !== 'string' || (source && row.source !== source)) throw invalid();
      const current = { windowEnd: row.window_end, id: row.id as number };
      if (previous && (current.windowEnd > previous.windowEnd || (current.windowEnd === previous.windowEnd && current.id >= previous.id))) throw invalid();
      previous = current; seen.add(current.id); items.push(normalizeMetricEnvelope(row));
    }
    if (next === null) return { items };
    cursor = next as string;
  }
  // As before, the console presents at most 200 observations, not the archive.
  return { items, nextCursor: cursor };
}

export const consoleApi = {
  apolloCredentials: () => apolloCredentialRequest(),
  saveApolloCredentials: (apiKey: string, expectedRevision: number, csrfToken: string) => {
    validCredentialRevision(expectedRevision);
    if (apiKey.length < 16 || apiKey.length > 4096 || /\s/.test(apiKey)) throw new Error('Use an Apollo API key between 16 and 4096 characters without whitespace.');
    return apolloCredentialRequest({ method: 'POST', body: JSON.stringify({ api_key: apiKey, expected_revision: expectedRevision }) }, csrfToken);
  },
  removeApolloCredentials: (expectedRevision: number, csrfToken: string) => {
    validCredentialRevision(expectedRevision);
    return apolloCredentialRequest({ method: 'DELETE', body: JSON.stringify({ expected_revision: expectedRevision }) }, csrfToken);
  },
  apolloBudget: async () => normalizeApolloBudget(await requestJson<unknown>(API_ROOT + '/prospects/enrichment/apollo')),
  apolloProspect: async (id: number) => normalizeApolloProspect(await requestJson<unknown>(API_ROOT + '/prospects/' + id + '/enrichment/apollo')),
  apolloContact: apolloPrivateContact,
  reviewApolloContact: async (id: number, resultId: number, expectedUpdatedAt: string, csrfToken: string) => {
    const generation = sessionGeneration();
    try {
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(resultId) || resultId <= 0 || !expectedUpdatedAt) throw new Error('Invalid identity');
      const response = record(await requestJson<unknown>(API_ROOT + '/prospects/' + id + '/enrichment/apollo/review', {
        method: 'POST', body: JSON.stringify({ expected_result_id: String(resultId), expected_updated_at: expectedUpdatedAt })
      }, csrfToken));
      assertCurrentSessionGeneration(generation);
      const job = record(response.job);
      if (!Number.isSafeInteger(job.id) || number(job.id) <= 0 || job.kind !== 'APOLLO_CONTACT_REVIEW') throw new Error('Invalid accepted job');
      return { jobId: String(job.id) };
    } catch (error) {
      assertCurrentSessionGeneration(generation);
      if (error instanceof StaleSessionError) throw error;
      throw new ConsoleApiError(error instanceof ConsoleApiError ? error.status : 0, 'The Director review could not be confirmed. Refresh its status before trying again.');
    }
  },
  enrichProspectApollo: async (id: number, expectedUpdatedAt: string, csrfToken: string) => {
    if (!Number.isSafeInteger(id) || id <= 0 || !expectedUpdatedAt) throw new Error('Current prospect evidence is required.');
    const response = record(await requestJson<unknown>(API_ROOT + '/prospects/' + id + '/enrichment/apollo', {
      method: 'POST', body: JSON.stringify({ confirmed_no_send: true, expected_updated_at: expectedUpdatedAt })
    }, csrfToken));
    const idValue = record(response.job).id;
    if (!Number.isSafeInteger(idValue) || number(idValue) <= 0) throw new Error('The accepted job could not be confirmed. Check Apollo status before retrying.');
    return { jobId: String(idValue) };
  },
  prospects: (options: ListOptions = {}) => requestJson<ProspectPage>(listUrl('prospects', options)),
  prospect: (id: number) => requestJson<Prospect>(API_ROOT + '/prospects/' + id),
  prospectConcept: (id: number) => requestJson<ProspectConcept>(API_ROOT + '/prospects/' + id + '/concept'),
  generateProspectPreview: async (id: number, expectedUpdatedAt: string, csrfToken: string) => {
    const response = record(await requestJson<unknown>(API_ROOT + '/prospects/' + id + '/preview', { method: 'POST', body: JSON.stringify({ expected_updated_at: expectedUpdatedAt }) }, csrfToken));
    return normalizeJob(response.job);
  },
  supplyProspectContact: (id: number, payload: { contact_email: string; source_url: string; business_contact_confirmed: true; expected_updated_at: string }, csrfToken: string) => requestJson<Prospect>(API_ROOT + '/prospects/' + id + '/contact', { method: 'POST', body: JSON.stringify(payload) }, csrfToken),
  requestProspectReview: async (id: number, expectedUpdatedAt: string, mode: 'review' | 'revise', csrfToken: string) => {
    const response = record(await requestJson<unknown>(API_ROOT + '/prospects/' + id + '/outreach-review', { method: 'POST', body: JSON.stringify({ expected_updated_at: expectedUpdatedAt, mode }) }, csrfToken));
    return normalizeJob(record(response.job));
  },
  previews: () => requestJson<{ items: PrivatePreviewSummary[] }>(API_ROOT + '/previews'),
  preview: (id: string) => requestJson<PrivatePreview>(API_ROOT + '/previews/' + encodeURIComponent(id)),
  researchProspects: async (payload: { motion: 'PARTNER' | 'DIRECT_VENUE'; segment: string; geography: string; objective: string; limit: number }, csrfToken: string) => {
    const response = record(await requestJson<unknown>(API_ROOT + '/prospect-research', { method: 'POST', body: JSON.stringify(payload) }, csrfToken));
    return { jobId: String(record(response.job).id ?? '') };
  },
  reviewProspect: (id: number, decision: 'APPROVE' | 'REJECT' | 'SUPPRESS', csrfToken: string) => requestJson<Prospect>(API_ROOT + '/prospects/' + id + '/review', { method: 'POST', body: JSON.stringify({ decision }) }, csrfToken),
  prospectOutreachPause: (paused: boolean, csrfToken: string) => requestJson<{ paused: boolean }>(API_ROOT + '/prospect-outreach/pause', { method: 'POST', body: JSON.stringify({ paused }) }, csrfToken),
  prospectOutreachCycle: (csrfToken: string) => requestJson<{ accepted: boolean }>(API_ROOT + '/prospect-outreach/cycle', { method: 'POST' }, csrfToken),
  mailSenderCheck: (csrf: string) => requestJson(API_ROOT + '/mail/sender/check', { method: 'POST' }, csrf),
  mailGoogleClient: (client_id: string, client_secret: string, csrf: string) => requestJson(API_ROOT + '/mail/oauth/client', { method: 'POST', body: JSON.stringify({ client_id, client_secret }) }, csrf),
  mailGoogleStart: (csrf: string) => requestJson<{ authorization_url: string }>(API_ROOT + '/mail/oauth/start', { method: 'POST' }, csrf),
  mailGoogleComplete: (state: string, code: string, csrf: string) => requestJson(API_ROOT + '/mail/oauth/complete', { method: 'POST', body: JSON.stringify({ state, code }) }, csrf),
  mailStatus: () => requestJson<MailStatus>(API_ROOT + '/mail/status'),
  mailThreads: (status = '', offset = 0) => requestJson<{ items: MailThreadSummary[]; has_more: boolean }>(API_ROOT + '/mail/threads?' + new URLSearchParams({ offset: String(offset), ...(status ? { status } : {}) })),
  mailThread: (id: number) => requestJson<MailThread>(API_ROOT + '/mail/threads/' + id),
  mailPause: (paused: boolean, csrfToken: string, id?: number) => requestJson(API_ROOT + '/mail/' + (id ? 'threads/' + id + '/' : '') + 'pause', { method: 'POST', body: JSON.stringify({ paused }) }, csrfToken),
  mailCycle: (csrfToken: string) => requestJson(API_ROOT + '/mail/cycle', { method: 'POST' }, csrfToken),
  session: () => requestJson<SessionResponse>('/auth/session'),
  login: (username: string, password: string, csrfToken: string) => requestJson<SessionResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }, csrfToken),
  logout: (csrfToken: string) => requestJson<void>('/auth/logout', { method: 'POST' }, csrfToken),
  status: async () => normalizeDashboard(...await Promise.all([
    requestJson<unknown>(API_ROOT + '/status'), requestJson<unknown>(API_ROOT + '/capabilities')
  ]) as [unknown, unknown]),
  metrics: metricHistory,
  collect: async (days: number, csrfToken: string) => {
    const response = record(await requestJson<unknown>(API_ROOT + '/collect', { method: 'POST', body: JSON.stringify({ days }) }, csrfToken));
    return { jobId: String(record(response.job).id ?? '') };
  },
  runAgent: async (agent: string, instruction: string, csrfToken: string) => {
    const response = record(await requestJson<unknown>(API_ROOT + '/agents/' + encodeURIComponent(agent) + '/runs', { method: 'POST', body: JSON.stringify({ instruction }) }, csrfToken));
    return { jobId: String(record(response.job).id ?? '') };
  },
  job: async (id: number): Promise<ManagementJob> => normalizeJob(await requestJson<unknown>(API_ROOT + '/jobs/' + id)),
  reports: async (options: { agent?: string; search?: string; since?: string; cursor?: string | null } = {}): Promise<ListResponse<ReportSummary>> => {
    const params = new URLSearchParams({ limit: '20' });
    Object.entries(options).forEach(([key, value]) => { if (value) params.set(key, value); });
    const result = await rawPage(API_ROOT + '/reports?' + params.toString());
    return { items: pageItems(result).map((item) => ({
      id: number(item.run_id), agent: text(item.agent, 'unknown'), createdAt: text(item.created_at),
      title: text(item.report_title) || `${text(item.agent, 'Agent')} report #${number(item.run_id)}`, summary: text(item.report_summary)
    })), nextCursor: nullableText(record(record(result).page).next_cursor) };
  },
  work: (status = '', cursor: string | null = null) => requestJson<WorkPageResponse>(API_ROOT + '/work?' + new URLSearchParams({ limit: '30', ...(status ? { status } : {}), ...(cursor ? { cursor } : {}) })),
  workDetail: (id: number) => requestJson<WorkOrder>(API_ROOT + '/work/' + id),
  workCycle: (csrfToken: string) => requestJson<{ accepted: boolean }>(API_ROOT + '/work-cycle', { method: 'POST' }, csrfToken),
  guidance: () => requestJson<{ items: AgentGuidance[] }>(API_ROOT + '/agent-guidance'),
  report: async (id: number): Promise<Report> => {
    const item = record(await requestJson<unknown>(API_ROOT + '/reports/' + id));
    return { id: number(item.run_id), agent: text(item.agent, 'unknown'), createdAt: text(item.created_at), title: `${text(item.agent, 'Agent')} report #${number(item.run_id)}`, markdown: text(item.report_markdown) };
  },
  experiments: async (): Promise<ListResponse<Experiment>> => ({ items: pageItems(await rawPage(API_ROOT + '/experiments?limit=200&include_final=true')).map(normalizeExperiment) }),
  createExperiment: (payload: Record<string, unknown>, csrfToken: string) => requestJson<unknown>(API_ROOT + '/experiments', { method: 'POST', body: JSON.stringify(payload) }, csrfToken),
  transitionExperiment: (id: string, payload: Record<string, unknown>, csrfToken: string) => requestJson<Experiment>(API_ROOT + '/experiments/' + encodeURIComponent(id) + '/transition', { method: 'POST', body: JSON.stringify(payload) }, csrfToken),
  addCommercialSnapshot: (payload: Record<string, unknown>, csrfToken: string) => requestJson<unknown>(API_ROOT + '/commercial-snapshots', { method: 'POST', body: JSON.stringify(payload) }, csrfToken),
  runs: (options: ListOptions = {}): Promise<ListResponse<AgentRun>> => listPage('runs', options, normalizeRun),
  backlog: (options: ListOptions = {}): Promise<ListResponse<BacklogItem>> => listPage('backlog', options, (item) => ({
    id: number(item.id), title: text(item.title, 'Untitled backlog item'), priority: String(number(item.priority)), status: text(item.status, 'UNKNOWN'), createdAt: text(item.created_at),
    hypothesis: text(item.hypothesis), metric: text(item.metric), notes: text(item.notes), stateSha256: text(item.state_sha256)
  })),
  createBacklog: (payload: Record<string, unknown>, csrfToken: string) => requestJson<unknown>(API_ROOT + '/backlog', { method: 'POST', body: JSON.stringify(payload) }, csrfToken),
  updateBacklog: (id: number, payload: Record<string, unknown>, csrfToken: string) => requestJson<unknown>(API_ROOT + '/backlog/' + id, { method: 'PATCH', body: JSON.stringify(payload) }, csrfToken),
  decisions: (options: ListOptions = {}): Promise<ListResponse<DecisionItem>> => listPage('decisions', options, (item) => ({
    id: number(item.id), agent: text(item.agent, 'unknown'), decision: text(item.decision), rationale: text(item.rationale, 'No rationale recorded.'), createdAt: text(item.created_at)
  })),
  commercialSnapshots: async (): Promise<ListResponse<CommercialSnapshot>> => ({ items: pageItems(await rawPage(API_ROOT + '/commercial-snapshots?limit=100')).map((item) => ({
    id: number(item.id), effectiveAt: text(item.effective_at), sourceLabel: text(item.source_label), currency: 'EUR', evidenceRef: text(item.evidence_ref), metrics: numericMetrics(item.metrics)
  })) }),
  outbox: (options: ListOptions = {}): Promise<ListResponse<OutboxItem>> => listPage('outbox', options, normalizeOutbox),
  outboxItem: async (id: number): Promise<OutboxItem> => normalizeOutbox(record(await requestJson<unknown>(API_ROOT + '/outbox/' + id))),
  updateOutbox: async (id: number, payload: Record<string, unknown>, csrfToken: string) => {
    const response = record(await requestJson<unknown>(API_ROOT + '/outbox/' + id, { method: 'PUT', body: JSON.stringify(payload) }, csrfToken));
    return normalizeOutbox(record(response.outbox));
  },
  approveContent: (id: number, payload: { expectedPayloadSha256: string; publicationState: string | null; expectedRevision: number | null }, csrfToken: string) => requestJson<OutboxItem>(API_ROOT + '/outbox/' + id + '/approve', {
    method: 'POST', body: JSON.stringify({ expected_payload_sha256: payload.expectedPayloadSha256, publication_state: payload.publicationState, expected_revision: payload.expectedRevision })
  }, csrfToken),
  publishContent: (id: number, expectedApprovedPayloadSha256: string, csrfToken: string) => {
    if (!/^[0-9a-f]{64}$/.test(expectedApprovedPayloadSha256)) {
      return Promise.reject(new Error('Approved payload evidence is required for publication'));
    }
    return requestJson<ContentPublishResponse>(API_ROOT + '/outbox/' + id + '/publish', {
      method: 'POST', body: JSON.stringify({ expected_approved_payload_sha256: expectedApprovedPayloadSha256 })
    }, csrfToken);
  },
  audit: (options: ListOptions = {}): Promise<ListResponse<AuditEvent>> => listPage('audit', options, (item) => ({
    id: String(item.id ?? ''), occurredAt: text(item.occurred_at), actor: text(item.actor, 'unknown'), action: text(item.action), objectType: text(item.resource_type), objectId: text(item.resource_id), outcome: text(item.outcome, 'unknown'), reasonCode: text(item.reason_code), requestId: text(item.request_id)
  }))
};

export const disabledCapabilities: Capabilities = {
  view: false, collectMetrics: false, runAgents: false, createExperiments: false,
  manageCommercial: false, manageProspects: false, editOutbox: false, manageBacklog: false, approveContent: false,
  publishContent: false, viewAudit: false
};
export function normalizeCapabilities(value?: Partial<Capabilities>): Capabilities {
  return { ...disabledCapabilities, ...(value || {}) };
}
